// Signup page — visually mirrors the desktop's `AuthForm` card. Posts to
// `/api/v1/auth/signup` on the orchestrator. The first user to sign up is
// auto-promoted to admin by the orchestrator (see UsersService.register).
import { useState } from "react";
import { ApiError, httpClient } from "../../client/http-client";
import { jwtStore } from "../../client/jwt-store";

const cardClassName =
	"tg-surface tg-text-primary w-full max-w-[560px] rounded-[18px] p-6 shadow-[var(--shadow-tg-elevated)]";
const inputClassName =
	"tg-input tg-text-primary h-12 w-full rounded-[16px] px-4";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.95rem] font-semibold disabled:cursor-wait disabled:opacity-60";

interface SignupPageProps {
	onAuthenticated: () => void;
	// True when this orchestrator has zero users — the only path that
	// reaches this component now. Future iterations may surface a
	// different copy if non-bootstrap signup is ever re-enabled.
	isInitialSetup?: boolean;
}

export const SignupPage = ({
	onAuthenticated,
	isInitialSetup = false,
}: SignupPageProps) => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async () => {
		setError(null);
		const trimmedEmail = email.trim();
		if (!trimmedEmail || !password) {
			setError("Enter your email and a password to create an account.");
			return;
		}
		if (password.length < 8) {
			setError("Password must be at least 8 characters.");
			return;
		}
		if (password !== confirm) {
			setError("Passwords don’t match.");
			return;
		}
		setBusy(true);
		try {
			const response = await httpClient.auth.signup({
				email: trimmedEmail,
				password,
			});
			jwtStore.set({
				token: response.token,
				expiresAt: response.expiresAt,
				user: response.user,
			});
			onAuthenticated();
		} catch (err) {
			if (err instanceof ApiError) {
				setError(
					err.code === "EMAIL_TAKEN"
						? "An account with that email already exists."
						: err.message,
				);
			} else {
				setError(err instanceof Error ? err.message : String(err));
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className={cardClassName}>
			<div>
				<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
					{isInitialSetup
						? "Initial setup — create the administrator"
						: "Create your SignalLens account"}
				</span>
				<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
					{isInitialSetup ? "Welcome" : "Get started"}
				</h2>
				<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
					{isInitialSetup
						? "This orchestrator has no users yet. Create the first account — it will be promoted to administrator and can manage all other users from inside the app."
						: "Create an account to host your own trading profiles. Each profile runs an isolated Telegram session with a dedicated MT5 API key."}
				</p>
			</div>

			<form
				className="mt-5 grid gap-3.5"
				onSubmit={(event) => {
					event.preventDefault();
					void handleSubmit();
				}}
			>
				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Email
					</span>
					<input
						className={inputClassName}
						autoFocus
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						placeholder="name@example.com"
						type="email"
						inputMode="email"
						autoComplete="email"
						disabled={busy}
					/>
				</label>

				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Password
					</span>
					<input
						className={inputClassName}
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						placeholder="At least 8 characters"
						type="password"
						autoComplete="new-password"
						disabled={busy}
					/>
				</label>

				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Confirm password
					</span>
					<input
						className={inputClassName}
						value={confirm}
						onChange={(event) => setConfirm(event.target.value)}
						placeholder="Re-enter the password"
						type="password"
						autoComplete="new-password"
						disabled={busy}
					/>
				</label>

				<button
					className={primaryButtonClassName}
					type="submit"
					disabled={busy}
				>
					{busy ? "Creating account..." : "Create account"}
				</button>
			</form>

			{error ? (
				<div className="tg-alert-error mt-4 rounded-2xl px-4 py-3" role="alert">
					{error}
				</div>
			) : null}
		</section>
	);
};
